import React from "react";
import { Lock } from "lucide-react";

export default function BrowserMockup({
  src,
  alt = "AstaEnterprise ERP Screenshot",
  title = "AstaEnterprise — Gold Store Management",
  url = "app.astaenterprise.id/dashboard",
  badge = "Live ERP",
  className = "",
  glow = true,
  children,
  aspectRatio = "aspect-[16/10]",
  priority = false,
}) {
  return (
    <div
      className={`relative group rounded-2xl p-1 transition-all duration-500 ${
        glow
          ? "bg-gradient-to-b from-amber-300/40 via-amber-100/30 to-transparent shadow-[0_20px_60px_-15px_rgba(217,119,6,0.18)]"
          : "bg-gradient-to-b from-zinc-200/80 to-zinc-100/50 shadow-lg shadow-black/[0.04]"
      } ${className}`}
    >
      {/* Outer ambient soft color wash */}
      {glow && (
        <div
          aria-hidden="true"
          className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-r from-amber-200/40 via-yellow-100/30 to-amber-300/30 blur-2xl opacity-80 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        />
      )}

      {/* Main browser container */}
      <div className="rounded-xl overflow-hidden border border-zinc-200/90 bg-white shadow-2xl flex flex-col">
        {/* Browser Top Bar */}
        <div className="flex items-center justify-between px-3 md:px-4 py-2.5 bg-zinc-100/90 border-b border-zinc-200/80 select-none">
          {/* Traffic Lights */}
          <div className="flex items-center gap-1.5 md:gap-2">
            <span className="h-2.5 w-2.5 md:h-3 md:w-3 rounded-full bg-[#FF5F56] border border-[#E0443E]/80 inline-block" />
            <span className="h-2.5 w-2.5 md:h-3 md:w-3 rounded-full bg-[#FFBD2E] border border-[#DEA123]/80 inline-block" />
            <span className="h-2.5 w-2.5 md:h-3 md:w-3 rounded-full bg-[#27C93F] border border-[#1AAB29]/80 inline-block" />
          </div>

          {/* Address / URL Bar */}
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-white border border-zinc-200 text-[11px] md:text-xs text-zinc-600 max-w-[200px] sm:max-w-xs md:max-w-md truncate font-mono shadow-sm">
            <Lock className="h-3 w-3 text-amber-600 shrink-0" />
            <span className="text-zinc-700 truncate">{url}</span>
          </div>

          {/* Right Status Badge */}
          <div className="flex items-center gap-2">
            {badge && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {badge}
              </span>
            )}
          </div>
        </div>

        {/* Browser Content Area */}
        <div className={`relative w-full bg-zinc-950 overflow-hidden ${aspectRatio}`}>
          {src ? (
            <picture>
              <source srcSet={src.replace(/\.png$/, ".webp")} type="image/webp" />
              <img
                src={src}
                alt={alt}
                width={1440}
                height={900}
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "auto"}
                decoding={priority ? "sync" : "async"}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.01]"
              />
            </picture>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}
